import {RuleConditionType} from '@frontapp/workflows/rules';
import _ from 'lodash';

import {Localizer} from '../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage} from '../../../libs/shared/core/src/helpers/internationalization/message';
import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {buildTeammateDisplayName} from '../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {CustomViewBundleRolesEnum, CustomViewTypesEnum} from '../../models/api/customViewModel';
import {RuleModel} from '../../models/api/ruleModel';
import {TagBehaviorsEnum, TagModel} from '../../models/api/tagModel';
import {InboxesFiltersEnum} from '../../models/url/models/inboxesUrlModel';
import {RuleCondition, RuleParametersDependencies} from '../../settings/rules/ruleConditionModel';
import {isRuleConditionOfType, splitANDconditions} from '../../settings/rules/ruleHelpers';
import {tagHasBehaviors} from './tagHelpers';

/*
 * Visual custom views.
 */

/**
 * Represents a custom view to be displayed somewhere in the UI.
 * The represented custom view may be a private tag, team tag, or a team custom view.
 */
export interface VisualCustomView {
  id: number;
  namespace: string;
  type: CustomViewTypesEnum;
  uid: string | undefined;
  tag: TagModel;
  isExpanded: boolean;
  nestingLevel: number;
  children: ReadonlyArray<VisualCustomView>;
  /** Allow rendering a hidden WorkspaceCustomViewItem during optimistic updates, to allow error handling when changing tag nesting. */
  isHidden?: boolean;
  /** Is the item visible in the workspace or not. For now, this is always true outside of edit mode. */
  isVisibleInWorkspace: boolean;
}

/*
 * P1 helpers.
 */

export function buildCustomViewPath(
  viewId: number,
  {filter = InboxesFiltersEnum.OPEN, isTeammateView}: {filter?: InboxesFiltersEnum; isTeammateView?: boolean},
) {
  return isTeammateView
    ? `/inboxes/teammates/views/${viewId}/${filter}`
    : `/inboxes/teams/views/${viewId}/${filter}`;
}

/** Compute a flat array of VisualCustomView by recursively adding the children of each item to an array. */
export function computeFlatVisualCustomViews(
  customViews: ReadonlyArray<VisualCustomView>,
): ReadonlyArray<VisualCustomView> {
  return customViews.reduce((currentChildren, customView) => {
    // Recursively flatten the children of the current item, to also include in the array.
    const customViewChildren = computeFlatVisualCustomViews(customView.children);

    return [...currentChildren, customView, ...customViewChildren];
  }, [] as ReadonlyArray<VisualCustomView>);
}

// Description for custom views computed on client-side.
// Temporary (awaiting descriptions on rule bundles backend).
export function computeCustomViewDescription(
  customViewRules: ReadonlyArray<RuleModel>,
  dependencies: RuleParametersDependencies,
  localize: Localizer,
): string {
  const metadataRulesConditions: ReadonlyArray<RuleCondition> = _(customViewRules)
    .filter((rule) => rule.role === CustomViewBundleRolesEnum.INCREMENT_METADATA)
    .map(splitANDconditions)
    .flatten()
    .flatten()
    .value();

  const addToViewRule = customViewRules.find(
    (rule) => rule.role === CustomViewBundleRolesEnum.ADD_TO_CUSTOM_VIEW,
  );
  if (!addToViewRule) {
    throw new Error(`Encountered custom view rules without ${CustomViewBundleRolesEnum.ADD_TO_CUSTOM_VIEW}`);
  }

  const addToViewConditions = splitANDconditions(addToViewRule);

  const conditionToString = (condition: RuleCondition) =>
    ruleConditionToString(condition, dependencies, localize);

  const nonMetadataConditions = addToViewConditions
    .map((cond) => cond[0])
    .filter(isDefined)
    .filter((condition) => condition.type !== RuleConditionType.HAS_CONVERSATION_METADATA);

  const maybeAnyInbox = !nonMetadataConditions.some((cond) => cond.type === RuleConditionType.INBOX_IN)
    ? 'is in any inbox'
    : undefined;

  return `Conversation ${[
    maybeAnyInbox,
    ...metadataRulesConditions.map(conditionToString),
    ...nonMetadataConditions.map(conditionToString),
  ]
    .filter(isDefined)
    .join(', ')}`;
}

/**
 * Tags individually may be allowed to be pinned to P1 workspaces. If they are, they have {@see TagBehaviorsEnum.CAN_PIN}.
 * A tag cannot be pinned specifically to teammate workspaces if it is a custom view (i.e. if it has {@see TagBehaviorsEnum.CAN_CUSTOM_VIEW}.
 */
export function canBePinnedToTeammateWorkspace(tag: TagModel): boolean {
  return (
    tagHasBehaviors(tag, TagBehaviorsEnum.CAN_PIN) && !tagHasBehaviors(tag, TagBehaviorsEnum.CAN_CUSTOM_VIEW)
  );
}

/**
 * Tags individually may be allowed to be pinned to P1 workspaces. If they are, they have {@see TagBehaviorsEnum.CAN_PIN}.
 * A tag can only be pinned to team workspaces if it is a custom view (i.e. if it has {@see TagBehaviorsEnum.CAN_CUSTOM_VIEW}.
 * To get pinned to a specific team workspace, it must be in that team namespace.
 */
export function canBePinnedToTeamWorkspace(tag: TagModel, teamNamespace: string): boolean {
  return (
    tag.namespace === teamNamespace &&
    tagHasBehaviors(tag, TagBehaviorsEnum.CAN_PIN, TagBehaviorsEnum.CAN_CUSTOM_VIEW)
  );
}

function ruleConditionToString(
  condition: RuleCondition,
  dependencies: RuleParametersDependencies,
  localize: Localizer,
): string | undefined {
  if (isRuleConditionOfType(condition, RuleConditionType.INBOX_IN)) {
    const inboxes = condition.params.inboxes
      ?.map((inboxId) => dependencies.inboxes.find((i) => i.id === inboxId))
      .filter(isDefined);
    return localize(
      defineMessage({
        id: 'rulecondition-isininbox',
        message: `is in inbox ${inboxes?.map((i) => i.name).join(', ')}`,
      }),
    );
  }

  if (condition.type === RuleConditionType.IS_OPEN) {
    return localize(
      defineMessage({
        id: 'rulecondition-isopen',
        message: 'is open',
      }),
    );
  }

  if (condition.type === RuleConditionType.IS_ARCHIVED) {
    return localize(
      defineMessage({
        id: 'rulecondition-isarchived',
        message: 'is archived',
      }),
    );
  }

  if (condition.type === RuleConditionType.IS_SNOOZED) {
    return localize(
      defineMessage({
        id: 'rulecondition-issnoozed',
        message: 'is snoozed',
      }),
    );
  }

  if (condition.type === RuleConditionType.IS_UNASSIGNED) {
    return localize(
      defineMessage({
        id: 'rulecondition-isunassigned',
        message: 'is unassigned',
      }),
    );
  }

  if (isRuleConditionOfType(condition, RuleConditionType.ASSIGNEE_IS)) {
    const teammate = dependencies.teammates.find((tea) => tea.id === condition.params.teammate);
    if (!teammate) {
      return undefined;
    }

    return localize(
      defineMessage({
        id: 'rulecondition-isassigned',
        message: `is assigned to ${buildTeammateDisplayName(teammate)}`,
      }),
    );
  }
  if (isRuleConditionOfType(condition, RuleConditionType.ASSIGNEE_IN, RuleConditionType.ASSIGNEE_NOT_IN)) {
    const teammateNames = dependencies.teammates
      .filter((teammate) => condition.params.teammates.includes(teammate.id))
      .map((teammate) => buildTeammateDisplayName(teammate))
      .filter(isDefined);

    if (!teammateNames.length) {
      return undefined;
    }

    if (condition.type === RuleConditionType.ASSIGNEE_NOT_IN) {
      return localize(
        defineMessage({
          id: 'rulecondition-assigneenot',
          message: `is not assigned to ${teammateNames.join(', ')}`,
        }),
      );
    }

    return localize(
      defineMessage({
        id: 'rulecondition-assignee',
        message: `is assigned to ${teammateNames.join(', ')}`,
      }),
    );
  }
  if (isRuleConditionOfType(condition, RuleConditionType.CHANNEL_IN)) {
    const channels = (condition.params.channels || [])
      .map((channelId) => dependencies.channels.find((c) => c.id === channelId))
      .filter(isDefined);

    return localize(
      defineMessage({
        id: 'rulecondition-channelin',
        message: `is in channel ${channels.map((c) => c.displayName).join(', ')}`,
      }),
    );
  }
  if (
    isRuleConditionOfType(condition, RuleConditionType.UNASSIGNED_AFTER, RuleConditionType.UNREPLIED_AFTER)
  ) {
    const {delay} = condition.params;
    if (!delay) {
      return undefined;
    }

    const {unit, val} = delay;

    const verb = condition.type === RuleConditionType.UNASSIGNED_AFTER ? 'unassigned' : 'unreplied';

    return localize(
      defineMessage({
        id: 'rulecondition-untouchedafter',
        message: `is ${verb} after ${val} ${unit}`,
      }),
    );
  }
  if (
    isRuleConditionOfType(
      condition,
      RuleConditionType.MESSAGE_FROM,
      RuleConditionType.MESSAGE_TO,
      RuleConditionType.MESSAGE_CC,
      RuleConditionType.MESSAGE_BCC,
      RuleConditionType.MESSAGE_TO_CC,
      RuleConditionType.MESSAGE_ANY_RECIPIENT,
      RuleConditionType.SUBJECT,
      RuleConditionType.MESSAGE_BODY,
      RuleConditionType.COMMENT_BODY,
    )
  ) {
    const {text, comparator} = condition.params;

    const manifestCondition = dependencies.integrations.conditions.find((cond) => cond.id === condition.type);
    const conditionLabel = manifestCondition?.name ?? condition.type;

    return `${conditionLabel} ${comparator} ${text}`;
  }
  return undefined;
}
